import { isNumeric } from '../../util/helpers';
/**
 * Terminal procedure document type
 */
export var ProcedureType;
(function (ProcedureType) {
    /**
     * Airport surface hot spot document
     */
    ProcedureType[ProcedureType["HotSpot"] = 0] = "HotSpot";
    /**
     * Airport diagram
     */
    ProcedureType[ProcedureType["AirportDiagram"] = 1] = "AirportDiagram";
    /**
     * Standard terminal arrival route (STAR)
     */
    ProcedureType[ProcedureType["STAR"] = 2] = "STAR";
    /**
     * Alternate or takeoff minimums
     */
    ProcedureType[ProcedureType["TakeoffMinimums"] = 3] = "TakeoffMinimums";
    /**
     * Instrument approach procedure (IAP)
     */
    ProcedureType[ProcedureType["IAP"] = 4] = "IAP";
    /**
     * Land and hold short operations (LAHSO)
     */
    ProcedureType[ProcedureType["LAHSO"] = 5] = "LAHSO";
    /**
     * Departure procedure (DP)
     */
    ProcedureType[ProcedureType["DP"] = 6] = "DP";
    /**
     * "Attention All Users" page (AAUP)
     */
    ProcedureType[ProcedureType["AAUP"] = 7] = "AAUP";
    /**
     * Obstacle departure procedure (ODP)
     */
    ProcedureType[ProcedureType["ODP"] = 8] = "ODP";
    /**
     * Chart supplement
     */
    ProcedureType[ProcedureType["CHS"] = 9] = "CHS";
})(ProcedureType || (ProcedureType = {}));
/**
 * For new or recently updated terminal procedures, indicates the type of change that occurred in the last data cycle.
 */
export var ChangeType;
(function (ChangeType) {
    ChangeType[ChangeType["Added"] = 0] = "Added";
    ChangeType[ChangeType["Changed"] = 1] = "Changed";
    ChangeType[ChangeType["Deleted"] = 2] = "Deleted";
})(ChangeType || (ChangeType = {}));
/**
 * Terminal procedure data cycle model.
 */
export var TerminalProcedureCycle;
(function (TerminalProcedureCycle) {
    /**
     * Represents terminal procedures that are effective in the current data cycle.
     */
    TerminalProcedureCycle["Current"] = "current";
    /**
     * Represents terminal procedures that will become effective in the next data cycle.
     */
    TerminalProcedureCycle["Next"] = "next";
})(TerminalProcedureCycle || (TerminalProcedureCycle = {}));
/**
 * Represents the available formats in which a batch of terminal procedures can be downloaded.
 */
export var TerminalProcedureBatchFormat;
(function (TerminalProcedureBatchFormat) {
    TerminalProcedureBatchFormat["PDF"] = "pdf";
    TerminalProcedureBatchFormat["ZIP"] = "zip";
})(TerminalProcedureBatchFormat || (TerminalProcedureBatchFormat = {}));
/**
 * Terminal procedure data for an airport
 */
var TerminalProcedure = /** @class */ (function () {
    function TerminalProcedure() {
    }
    Object.defineProperty(TerminalProcedure.prototype, "fullFilename", {
        /**
         * Returns the full filename of the procedure (including the volume and PDF name). This is the filename that
         * should be used when using the batch utility to download multiple procedures as a single PDF or ZIP file.
         * @returns {string}
         */
        get: function () {
            return this.volume + "/" + this.filename;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Parses terminal procedure
     * @param data {any} Terminal procedure data as returned by API
     * @return {TerminalProcedure} Parsed terminal procedure
     */
    TerminalProcedure.parse = function (data) {
        return Object.assign(new TerminalProcedure(), {
            id: data.terminalProcedureId,
            airportId: data.airportId,
            volume: data.volume,
            filename: data.pageName,
            type: this.parseProcedureType(data.pageType),
            filesize: isNumeric(data.pdfSize) ? +data.pdfSize : null,
            effDateFrom: data.effDateFrom ? new Date(data.effDateFrom) : null,
            effDateTo: data.effDateTo ? new Date(data.effDateTo) : null,
            url: data.url,
            military: data.military === 'M',
            changeType: this.parseChangeType(data.userAction),
            name: data.chartName
        });
    };
    /**
     * Parses procedure type
     * @param value {any} Procedure type as returned by API
     * @return {ProcedureType} Parsed procedure type
     */
    TerminalProcedure.parseProcedureType = function (value) {
        switch (value) {
            case 'HOT':
                return ProcedureType.HotSpot;
            case 'APD':
                return ProcedureType.AirportDiagram;
            case 'STAR':
                return ProcedureType.STAR;
            case 'MIN':
                return ProcedureType.TakeoffMinimums;
            case 'IAP':
                return ProcedureType.IAP;
            case 'LAH':
                return ProcedureType.LAHSO;
            case 'DP':
                return ProcedureType.DP;
            case 'DAU':
                return ProcedureType.AAUP;
            case 'ODP':
                return ProcedureType.ODP;
            case 'CHS':
                return ProcedureType.CHS;
            default:
                return null;
        }
    };
    /**
     * Formats procedure type (abbreviation) for dislay
     * @param value {ProcedureType} Procedure type
     * @return {string} Formatted procedure type
     */
    TerminalProcedure.formatProcedureType = function (value) {
        switch (value) {
            case ProcedureType.HotSpot:
                return 'HOT';
            case ProcedureType.AirportDiagram:
                return 'APD';
            case ProcedureType.STAR:
                return 'STAR';
            case ProcedureType.TakeoffMinimums:
                return 'MIN';
            case ProcedureType.IAP:
                return 'IAP';
            case ProcedureType.LAHSO:
                return 'LAHSO';
            case ProcedureType.DP:
                return 'DP';
            case ProcedureType.AAUP:
                return 'AAUP';
            case ProcedureType.ODP:
                return 'ODP';
            case ProcedureType.CHS:
                return 'CHS';
            default:
                return null;
        }
    };
    /**
     * Formats procedure type (full term) for dislay
     * @param value {ProcedureType} Procedure type
     * @param [plural] {boolean} Plural (defaults to false)
     * @return {string} Formatted procedure type
     */
    TerminalProcedure.formatProcedureTypeFull = function (value, plural) {
        if (plural === void 0) { plural = false; }
        switch (value) {
            case ProcedureType.HotSpot:
                return 'Hot Spot Diagram' + (plural ? 's' : '');
            case ProcedureType.AirportDiagram:
                return 'Airport Diagram' + (plural ? 's' : '');
            case ProcedureType.STAR:
                return 'STAR' + (plural ? 's' : '');
            case ProcedureType.TakeoffMinimums:
                return 'Takeoff Minimums';
            case ProcedureType.IAP:
                return 'Approach Procedure' + (plural ? 's' : '');
            case ProcedureType.LAHSO:
                return 'LAHSO';
            case ProcedureType.DP:
                return 'Departure Procedure' + (plural ? 's' : '');
            case ProcedureType.AAUP:
                return '"Attention All Users" Page';
            case ProcedureType.ODP:
                return 'Obstacle Departure Procedure' + (plural ? 's' : '');
            case ProcedureType.CHS:
                return 'Chart Supplement' + (plural ? 's' : '');
            default:
                return null;
        }
    };
    /**
     * Parses change type
     * @param value {any} Change type as returned by API
     * @return {ChangeType} Parsed change type
     */
    TerminalProcedure.parseChangeType = function (value) {
        switch (value) {
            case 'A':
                return ChangeType.Added;
            case 'C':
                return ChangeType.Changed;
            case 'D':
                return ChangeType.Deleted;
            default:
                return null;
        }
    };
    /**
     * Formats procedure change type for display
     * @param value {ChangeType} Procedure change type
     * @return {string} Formatted procedure change type
     */
    TerminalProcedure.formatChangeType = function (value) {
        switch (value) {
            case ChangeType.Added:
                return 'New';
            case ChangeType.Changed:
                return 'Changed';
            case ChangeType.Deleted:
                return 'Removed';
            default:
                return null;
        }
    };
    return TerminalProcedure;
}());
export { TerminalProcedure };
/**
 * Model for a set of terminal procedures that are all effective during the same data cycle.
 */
var TerminalProcedureSet = /** @class */ (function () {
    function TerminalProcedureSet() {
    }
    /**
     * Parses terminal procedure set
     * @param data {any} Terminal procedure data as returned by API
     * @return {TerminalProcedure} Parsed terminal procedure set
     */
    TerminalProcedureSet.parse = function (data) {
        var procedures = (data || []).map(function (d) { return TerminalProcedure.parse(d); });
        return Object.assign(new TerminalProcedureSet(), {
            effDateFrom: this.getEffDateFrom(procedures),
            effDateTo: this.getEffDateTo(procedures),
            procedures: procedures
        });
    };
    /**
     * Returns the "effective from" date of the terminal procedure set. This is based on the effective date
     * of the first procedure (other than the chart supplement, which is based on a different data cycle schedule).
     * @param {Array<TerminalProcedure>} procedures
     * @returns {Date}
     */
    TerminalProcedureSet.getEffDateFrom = function (procedures) {
        var proc = procedures.filter(function (p) { return p.type !== ProcedureType.CHS; })[0];
        return proc ? proc.effDateFrom : null;
    };
    /**
     * Returns the "effective to" date of the terminal procedure set. This is based on the effective date
     * of the first procedure (other than the chart supplement, which is based on a different data cycle schedule).
     * @param {Array<TerminalProcedure>} procedures
     * @returns {Date}
     */
    TerminalProcedureSet.getEffDateTo = function (procedures) {
        var proc = procedures.filter(function (p) { return p.type !== ProcedureType.CHS; })[0];
        return proc ? proc.effDateTo : null;
    };
    return TerminalProcedureSet;
}());
export { TerminalProcedureSet };
/**
 * Model for a set of terminal procedures that are broken out by a "current" set (effective now),
 * and a "next" set (effective during the next data cycle).
 */
var TerminalProcedurePack = /** @class */ (function () {
    function TerminalProcedurePack() {
    }
    /**
     * Parses terminal procedure pack
     * @param current {any} Current terminal procedure data as returned by API
     * @param next {any} Upcoming terminal procedure data as returned by API
     * @return {TerminalProcedure} Parsed terminal procedure pack
     */
    TerminalProcedurePack.parse = function (current, next) {
        var result = Object.assign(new TerminalProcedurePack(), {
            current: TerminalProcedureSet.parse(current),
            next: next ? TerminalProcedureSet.parse(next) : null,
        });
        // Chart supplements are published on a 56-day cycle, whereas the rest of the terminal procedures are published
        // on a 28-day cycle. This can result in a discrepancy where the chart supplements are only included in "current",
        // but not in "next" - even though they are still valid in the "next" 28-day data cycle. Here we detect this
        // scenario, and copy the chart supplements from current into next if they are still valid.
        if (result.next && result.next.procedures.length >= 0 && result.next.effDateFrom) {
            var currentChartSupplements = result.current.procedures.filter(function (p) { return p.type === ProcedureType.CHS; });
            var _loop_1 = function (proc) {
                // Ensure the chart supplement is still valid in the next 28-day data cycle.
                if (proc.effDateTo.getTime() >= result.next.effDateFrom.getTime()) {
                    // Ensure the chart supplement is not already included in the next 28-day cycle.
                    if (result.next.procedures.findIndex(function (p) { return p.type === ProcedureType.CHS && p.airportId === proc.airportId; }) === -1) {
                        // Manually add the chart supplement to the next data cycle
                        result.next.procedures.push(proc);
                    }
                }
            };
            for (var _i = 0, currentChartSupplements_1 = currentChartSupplements; _i < currentChartSupplements_1.length; _i++) {
                var proc = currentChartSupplements_1[_i];
                _loop_1(proc);
            }
        }
        return result;
    };
    return TerminalProcedurePack;
}());
export { TerminalProcedurePack };
